import React, { useState, useCallback, createContext, useContext, ReactNode } from 'react';
import { FindTicketResponse, TicketStatus, PurchaseOldOrderResponse } from '../types/api';

export interface AppState {
  currentTicket: FindTicketResponse | null;
  orderResponse: PurchaseOldOrderResponse | null;
  isLoading: boolean;
  error: string | null;
  validationErrors: Record<string, string[]> | null;
  currentStep: 'home' | 'ticket-summary' | 'member-registration' | 'guest-registration' | 'ticket-void-waiting' | 'member-thank-you' | 'guest-thank-you' | 'contact-us';
  selectedFlow: 'member' | 'guest' | null;
  returnStep: AppState['currentStep'];
  appliedCouponCode: string | null;
}

interface AppContextType {
  state: AppState;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null, validationErrors?: Record<string, string[]> | null) => void;
  setTicket: (ticket: FindTicketResponse | null) => void;
  setOrderResponse: (orderResponse: PurchaseOldOrderResponse | null) => void;
  setStep: (step: AppState['currentStep']) => void;
  setReturnStep: (step: AppState['currentStep']) => void;
  setSelectedFlow: (flow: 'member' | 'guest' | null) => void;
  setAppliedCouponCode: (code: string | null) => void;
  resetState: () => void;
  getTicketStatusMessage: (status: TicketStatus) => string;
  canProceedToPayment: (status: TicketStatus) => boolean;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AppState>({
    currentTicket: null,
    orderResponse: null,
    isLoading: false,
    error: null,
    validationErrors: null,
    currentStep: 'home',
    selectedFlow: null,
    returnStep: 'home',
    appliedCouponCode: null,
  });

  const setLoading = useCallback((loading: boolean) => {
    setState(prev => ({ ...prev, isLoading: loading }));
  }, []);

  const setError = useCallback((error: string | null, validationErrors?: Record<string, string[]> | null) => {
    setState(prev => ({ 
      ...prev, 
      error, 
      validationErrors: validationErrors || null,
      isLoading: false 
    }));
  }, []);

  const setTicket = useCallback((ticket: FindTicketResponse | null) => {
    setState(prev => ({ 
      ...prev, 
      currentTicket: ticket,
      error: null,
      isLoading: false 
    }));
  }, []);

  const setOrderResponse = useCallback((orderResponse: PurchaseOldOrderResponse | null) => {
    setState(prev => ({ 
      ...prev, 
      orderResponse,
      error: null,
      isLoading: false 
    }));
  }, []);

  const setStep = useCallback((step: AppState['currentStep']) => {
    setState(prev => ({ ...prev, currentStep: step }));
  }, []);

  const setReturnStep = useCallback((step: AppState['currentStep']) => {
    setState(prev => ({ ...prev, returnStep: step }));
  }, []);

  const setSelectedFlow = useCallback((flow: 'member' | 'guest' | null) => {
    setState(prev => ({ ...prev, selectedFlow: flow }));
  }, []);

  const setAppliedCouponCode = useCallback((code: string | null) => {
    setState(prev => ({ ...prev, appliedCouponCode: code }));
  }, []);

  const resetState = useCallback(() => {
    setState({
      currentTicket: null,
      orderResponse: null,
      isLoading: false,
      error: null,
      validationErrors: null,
      currentStep: 'home',
      selectedFlow: null,
      returnStep: 'home',
      appliedCouponCode: null,
    });
  }, []);

  const getTicketStatusMessage = useCallback((status: TicketStatus): string => {
    switch (status) {
      case TicketStatus.active:
        return 'This ticket is ready for payment.';
      case TicketStatus.paid:
        return 'This ticket has already been paid.';
      case TicketStatus.voided:
        return 'This ticket will be paid automatically on exit.';
      case TicketStatus.unknown:
        return 'Unable to determine ticket status.';
      default:
        return 'Unknown ticket status.';
    }
  }, []);

  const canProceedToPayment = useCallback((status: TicketStatus): boolean => {
    return status === TicketStatus.active;
  }, []);

  const value: AppContextType = {
    state,
    setLoading,
    setError,
    setTicket,
    setOrderResponse,
    setStep,
    setReturnStep,
    setSelectedFlow,
    setAppliedCouponCode,
    resetState,
    getTicketStatusMessage,
    canProceedToPayment,
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};

export const useAppState = (): AppContextType => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useAppState must be used within an AppProvider');
  }
  return context;
}; 